import { create } from 'zustand';
import type {
  TaskListDto,
  TaskDto,
  CreateTaskRequest,
  UpdateTaskRequest,
} from '../types';
import { getTasks } from '../api/generated/tasks/tasks';
import type {
  FacilityFlowCoreModulesPlanningDtosTasksTaskListDto,
  FacilityFlowCoreModulesPlanningDtosTasksTaskDto,
  FacilityFlowCoreModulesPlanningDtosTasksCreateTaskRequest,
  FacilityFlowCoreModulesPlanningDtosTasksUpdateTaskRequest,
  GetApiV1TasksParams,
} from '../api/generated/models';
import { parseAxiosError, translateApiError } from '../errors';

const {
  getApiV1Tasks,
  getApiV1TasksId,
  postApiV1Tasks,
  putApiV1TasksId,
  deleteApiV1TasksId,
} = getTasks();

/** Key into `objectTasks`; active-only and all tasks of an object are cached separately. */
export const objectTasksKey = (objectId: string, activeOnly = false) =>
  `${objectId}|${activeOnly ? 'active' : 'all'}`;

const NO_TASKS: TaskListDto[] = [];

/** Selector for one object's tasks; stable empty array until they are loaded. */
export const selectObjectTasks = (objectId: string | undefined, activeOnly = false) =>
  (state: TaskState): TaskListDto[] =>
    (objectId && state.objectTasks[objectTasksKey(objectId, activeOnly)]) || NO_TASKS;

/** The object a task belongs to, from whatever task data is loaded (detail, full list, slices). */
function objectIdOfTask(state: TaskState, taskId: string): string | undefined {
  if (state.currentTask?.taskId === taskId) return state.currentTask.objectId;
  for (const list of [state.tasks, ...Object.values(state.objectTasks)]) {
    const match = list.find((task) => task.taskId === taskId);
    if (match) return match.objectId;
  }
  return undefined;
}

interface TaskState {
  /** All tasks of the tenant. Only `fetchTasks` writes it, so list pages never see a subset. */
  tasks: TaskListDto[];
  fullListLoaded: boolean;
  /**
   * Tasks per object (key: `objectTasksKey`), kept apart from `tasks` so a detail view or
   * picker loading one object's tasks cannot replace the full list other pages render.
   */
  objectTasks: Record<string, TaskListDto[]>;
  currentTask: TaskDto | null;
  loading: boolean;
  error: string | null;

  fetchTasks: () => Promise<void>;
  fetchObjectTasks: (objectId: string, options?: { activeOnly?: boolean }) => Promise<void>;
  /** Reloads whatever task data is in use after a change to a task of `objectId`. */
  refreshAfterChange: (objectId?: string | null) => Promise<void>;
  fetchTaskById: (id: string) => Promise<void>;
  createTask: (data: CreateTaskRequest) => Promise<TaskDto>;
  updateTask: (id: string, data: UpdateTaskRequest) => Promise<TaskDto>;
  /** Resolves to true when the task had history and was deactivated instead of deleted. */
  deleteTask: (id: string) => Promise<boolean>;
  clearCurrentTask: () => void;
  clearError: () => void;
}

const mapTaskListDto = (dto: FacilityFlowCoreModulesPlanningDtosTasksTaskListDto): TaskListDto => ({
  taskId: dto.taskId || '',
  serviceId: dto.serviceId || '',
  serviceName: dto.serviceName || '',
  objectId: dto.objectId || '',
  objectName: dto.objectName || '',
  name: dto.name || '',
  estimatedDurationMinutes: dto.estimatedDurationMinutes || 0,
  isActive: dto.isActive ?? true,
});

const mapTaskDto = (dto: FacilityFlowCoreModulesPlanningDtosTasksTaskDto): TaskDto => ({
  taskId: dto.taskId || '',
  tenantId: dto.tenantId || '',
  serviceId: dto.serviceId || '',
  serviceName: dto.serviceName || '',
  objectId: dto.objectId || '',
  objectName: dto.objectName || '',
  customerName: dto.customerName ?? null,
  name: dto.name || '',
  description: dto.description ?? null,
  estimatedDurationMinutes: dto.estimatedDurationMinutes || 0,
  materials: (dto.materials as unknown as TaskDto['materials']) ?? null,
  instructions: dto.instructions ?? null,
  isActive: dto.isActive ?? true,
  isBlocking: dto.isBlocking ?? false,
  createdAt: dto.createdAt || new Date().toISOString(),
  updatedAt: dto.updatedAt ?? null,
});

export const useTaskStore = create<TaskState>((set, get) => ({
  tasks: [],
  fullListLoaded: false,
  objectTasks: {},
  currentTask: null,
  loading: false,
  error: null,

  fetchTasks: async () => {
    set({ loading: true, error: null });
    try {
      const data = await getApiV1Tasks();
      set({ tasks: (data || []).map(mapTaskListDto), fullListLoaded: true, loading: false });
    } catch (err) {
      set({ error: translateApiError(parseAxiosError(err)), loading: false });
    }
  },

  fetchObjectTasks: async (objectId, options) => {
    const activeOnly = options?.activeOnly ?? false;
    set({ loading: true, error: null });
    try {
      const params: GetApiV1TasksParams = { objectId, isActive: activeOnly ? true : undefined };
      const data = await getApiV1Tasks(params);
      set((state) => ({
        objectTasks: {
          ...state.objectTasks,
          [objectTasksKey(objectId, activeOnly)]: (data || []).map(mapTaskListDto),
        },
        loading: false,
      }));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Fehler beim Laden der Aufgaben';
      set({ error: errorMessage, loading: false });
    }
  },

  refreshAfterChange: async (objectId) => {
    const { fullListLoaded, objectTasks } = get();
    const reloads: Promise<void>[] = [];
    if (fullListLoaded) reloads.push(get().fetchTasks());
    if (objectId) {
      for (const activeOnly of [false, true]) {
        if (objectTasksKey(objectId, activeOnly) in objectTasks) {
          reloads.push(get().fetchObjectTasks(objectId, { activeOnly }));
        }
      }
    }
    await Promise.all(reloads);
  },

  fetchTaskById: async (id: string) => {
    set({ loading: true, error: null });
    try {
      const data = await getApiV1TasksId(id);
      set({ currentTask: mapTaskDto(data), loading: false });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Fehler beim Laden der Aufgabe';
      set({ error: errorMessage, loading: false, currentTask: null });
    }
  },

  createTask: async (data: CreateTaskRequest) => {
    set({ loading: true, error: null });
    try {
      const payload: FacilityFlowCoreModulesPlanningDtosTasksCreateTaskRequest = {
        serviceId: data.serviceId,
        objectId: data.objectId,
        name: data.name ?? null,
        description: data.description ?? null,
        estimatedDurationMinutes: data.estimatedDurationMinutes,
        materials: (data.materials as unknown as FacilityFlowCoreModulesPlanningDtosTasksCreateTaskRequest['materials']) ?? null,
        instructions: data.instructions ?? null,
        isBlocking: data.isBlocking ?? false,
      };

      const result = await postApiV1Tasks(payload);

      // Reload the full list and this object's cached tasks, whichever are in use
      const objectId = data.objectId;
      await get().refreshAfterChange(objectId);

      set({ loading: false });
      return mapTaskDto(result as unknown as FacilityFlowCoreModulesPlanningDtosTasksTaskDto);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Fehler beim Erstellen der Aufgabe';
      set({ error: errorMessage, loading: false });
      throw err;
    }
  },

  updateTask: async (id: string, data: UpdateTaskRequest) => {
    set({ loading: true, error: null });
    try {
      const payload: FacilityFlowCoreModulesPlanningDtosTasksUpdateTaskRequest = {
        name: data.name ?? null,
        description: data.description ?? null,
        estimatedDurationMinutes: data.estimatedDurationMinutes,
        materials: (data.materials as unknown as FacilityFlowCoreModulesPlanningDtosTasksUpdateTaskRequest['materials']) ?? null,
        instructions: data.instructions ?? null,
        isActive: data.isActive,
        isBlocking: data.isBlocking ?? false,
      };

      const result = await putApiV1TasksId(id, payload);
      const updated = mapTaskDto(result as unknown as FacilityFlowCoreModulesPlanningDtosTasksTaskDto);

      // The saved task names its object, so the right cached tasks reload wherever it was edited.
      await get().refreshAfterChange(updated.objectId);

      set({ currentTask: updated, loading: false });
      return updated;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Fehler beim Aktualisieren der Aufgabe';
      set({ error: errorMessage, loading: false });
      throw err;
    }
  },

  deleteTask: async (id: string) => {
    set({ loading: true, error: null });
    try {
      // Looked up before deleting; afterwards the task is gone from every list.
      const objectId = objectIdOfTask(get(), id);
      const result = await deleteApiV1TasksId(id);

      await get().refreshAfterChange(objectId);

      set({ loading: false, currentTask: null });
      return result.deactivated ?? false;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Fehler beim Löschen der Aufgabe';
      set({ error: errorMessage, loading: false });
      throw err;
    }
  },

  clearCurrentTask: () => set({ currentTask: null }),
  clearError: () => set({ error: null }),
}));
